import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { publicEnv } from '@/lib/env';
import {
  LEADERBOARD_V2_TAB_OPTIONS,
  LEADERBOARD_V2_DEFAULT_TAB_VALUE,
  COLUMN_HEADERS,
} from '@/modules/homepage/RenderRankingsV2/constants';
import { RankingsV2Row } from '@/modules/homepage/RenderRankingsV2/RankingsV2Row';
import { LeaderboardV2Option, LeaderboardV2Value } from '@/types/homepage.types';
import { WidgetPlayersRankingsV2Data } from '@/types/ranking.types';
import { getRankingsV2 } from '@/utils/actions/homepage/getRankingsV2';

export interface RenderRankingsV2Props {
  rankings: WidgetPlayersRankingsV2Data[];
  metadata: LeaderboardV2Option;
}

export const RenderRankingsV2 = ({ rankings, metadata }: RenderRankingsV2Props) => {
  const [rankingValue, setRankingValue] = useState<LeaderboardV2Value>(
    metadata.tabValue || LEADERBOARD_V2_DEFAULT_TAB_VALUE.value
  );
  const [stopChangingTabs, setStopChangingTabs] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data: rankingsData, isFetching } = useQuery({
    queryKey: ['rankings', rankingValue],
    queryFn: async () => {
      const result = await getRankingsV2({
        leaderboardV2Option: { ...metadata, tabValue: rankingValue },
      });
      return result.data;
    },
    initialData: () => (rankingValue === metadata.tabValue ? rankings : undefined),
    staleTime: 5 * 60 * 1000,
    enabled: rankingValue !== metadata.tabValue,
  });

  useEffect(() => {
    if (metadata.changeTabs && !stopChangingTabs) {
      intervalRef.current = setInterval(() => {
        const currentIndex = LEADERBOARD_V2_TAB_OPTIONS.findIndex(
          (option) => option.value === rankingValue
        );
        const nextIndex = (currentIndex + 1) % LEADERBOARD_V2_TAB_OPTIONS.length;
        const nextTab = LEADERBOARD_V2_TAB_OPTIONS[nextIndex];

        if (nextTab) {
          setRankingValue(nextTab.value);
        }
      }, metadata.secondsInBetweenChanges * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [rankingValue, metadata.secondsInBetweenChanges, metadata.changeTabs, stopChangingTabs]);

  const stopInterval = () => {
    setStopChangingTabs(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const selectTab = (value: LeaderboardV2Value) => {
    stopInterval();
    setRankingValue(value);
  };

  return (
    <div className='rounded-xl bg-white shadow-sm'>
      <div className='flex items-center gap-2 border-b border-gray-200 p-4'>
        <img
          className='h-4 w-auto'
          alt='WPR horizontal logo'
          src={getCDNURL(
            `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/other/WPR_Horizontal+Logo-color.png`,
            1600,
            95
          )}
        />
      </div>

      <div className='flex justify-center border-b border-gray-200 px-2'>
        {LEADERBOARD_V2_TAB_OPTIONS.map((option) => {
          const isCurrentSelected = rankingValue === option.value;

          return (
            <button
              key={option.value}
              type='button'
              className={clsx(
                'flex justify-center w-full px-2 py-3 border-b-2 font-semibold text-sm text-gray-500 hover:text-experiment',
                {
                  '!border-experiment !text-experiment': isCurrentSelected,
                  'border-transparent': !isCurrentSelected,
                }
              )}
              onClick={() => selectTab(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <div className='flex flex-col h-[512px] pt-6'>
        {isFetching ? (
          <div className='flex flex-1 h-full items-center justify-center'>
            <FontAwesomeIcon size='lg' icon={faSpinnerThird} spin />
          </div>
        ) : !rankingsData || rankingsData.length === 0 ? (
          <div className='flex flex-1 h-full items-center justify-center text-sm text-gray-600'>
            {formatText({ text: 'No data found!', role: TextRole.ErrorMessage })}
          </div>
        ) : (
          <div className='relative flex-1'>
            <div className='flex items-center font-normal text-xs text-gray-500 px-4'>
              {COLUMN_HEADERS.map((header) => (
                <div key={header.label} className={header.className}>
                  {formatText({ text: header.label, role: TextRole.TableColumnHeader })}
                </div>
              ))}
            </div>
            <div className='flex flex-col divide-y divide-gray-200 overflow-auto'>
              {rankingsData.map((player) => (
                <RankingsV2Row key={player.playerUuid} player={player} />
              ))}
            </div>
          </div>
        )}
        <CustomLink
          href={`/rankings${rankingValue ? `?type=8&gender=${rankingValue}` : ''}`}
          className='flex items-center justify-center border-t border-gray-200 py-4 font-semibold text-sm text-experiment underline-offset-4 hover:underline'
        >
          {formatText({ text: 'View Full Leaderboard', role: TextRole.TabLabel })}
        </CustomLink>
      </div>
    </div>
  );
};
